import { IGameState } from 'State/Game/IGameState';

export type Folder = { folderName: string; props: (Prop | Folder)[] };

export enum PropType {
    BUTTON,
    LIST,
    NUMBER,
    STRING,
    LABEL,
    TOGGLE,
    SLIDER,
    NUMBER_WITH_BUTTON,
    SLIDER_WITH_BUTTON,
    STRING_WITH_BUTTON,
    LIST_WITH_BUTTON,
}
export type PropButton = { type: PropType.BUTTON; propName: string; onPress: () => void };
export type PropList<T> = {
    type: PropType.LIST;
    propName: string;
    items: T[];
    initialValue: T;
    onChange: (value: T, index: number) => void;
    onStateChanged?: (state: IGameState) => T;
};
export type PropNumber = {
    type: PropType.NUMBER;
    propName: string;
    initialValue: number;
    onFinishChange: (value: number) => void;
    onStateChanged?: (state: IGameState) => number;
};
export type PropString = {
    type: PropType.STRING;
    propName: string;
    initialValue: string;
    onFinishChange: (value: string) => void;
    onStateChanged?: (state: IGameState) => string;
};

export type PropToggle = {
    type: PropType.TOGGLE;
    propName: string;
    initialValue: boolean;
    onChange: (value: boolean) => void;
    hide?: boolean;
    onStateChanged?: (state: IGameState) => boolean;
};

export type PropSlider = {
    type: PropType.SLIDER;
    propName: string;
    initialValue: number;
    min: number;
    max: number;
    step: number;
    onFinishChange: (value: number) => void;
    onStateChanged?: (state: IGameState) => number;
};

export type PropNumberWithButton = {
    type: PropType.NUMBER_WITH_BUTTON;
    propName: string;
    initialValue: number;
    buttonLabel: string;
    onButtonClick: (value: number) => void;
    onFinishChange: (value: number) => void;
    onStateChanged?: (state: IGameState) => number;
};

export type PropSliderWithButton = {
    type: PropType.SLIDER_WITH_BUTTON;
    propName: string;
    initialValue: number;
    min: number;
    max: number;
    step: number;
    buttonLabel: string;
    onButtonClick: (value: number) => void;
    onFinishChange: (value: number) => void;
    onStateChanged?: (state: IGameState) => number;
};

export type PropStringWithButton = {
    type: PropType.STRING_WITH_BUTTON;
    propName: string;
    initialValue: string;
    buttonLabel: string;
    onButtonClick: (value: string) => void;
    onFinishChange: (value: string) => void;
    onStateChanged?: (state: IGameState) => string;
};

export type PropLabel = {
    type: PropType.LABEL;
    propName: string;
    initialValue: string;
    onStateChanged?: (state: IGameState) => string;
};

export type PropListWithButton<T> = {
    type: PropType.LIST_WITH_BUTTON;
    propName: string;
    items: T[];
    initialValue: T;
    buttonLabel: string;
    onButtonClick: (value: T, index: number) => void;
    onFinishChange: (value: T, index: number) => void;
    onStateChanged?: (state: IGameState) => T;
};

export type Prop =
    | PropButton
    | PropList<any>
    | PropNumber
    | PropLabel
    | PropToggle
    | PropSlider
    | PropNumberWithButton
    | PropString
    | PropSliderWithButton
    | PropStringWithButton
    | PropListWithButton<any>;

export type DebugMenuOptions = {
    useLocalStorage?: boolean;
    showLanguage?: boolean;
    showOnStart?: boolean;
    props: (Prop | Folder)[];
    width?: number;
};

export type DebugMenu = {
    getProp: (propName: string) => dat.GUIController;
    getPropList: () => dat.GUIController[];
    updateHandlers: Record<string, (state: IGameState) => unknown>;
    gui: dat.GUI;
};

export default async function initDebugMenu() {
    const createDebugMenu = await import('./DebugMenu');
    return (createDebugMenu.default as unknown) as (options: DebugMenuOptions) => DebugMenu;
}
